import type { CARDS_ASPECT_RATIO, EVENT_CARD_ASPECT_RATIO } from 'blocks/Cards'
import type { CARDS_ASPECT_RATIO_VARS } from 'vars/cards-ascpect-ratio'
import type { CAROUSEL_PADDINGS, GRID, ROW_GAP } from 'vars/index'
import type {
  CellsAlignment,
  ISnapAlign,
  RowContainerVerticalGaps,
  SpecialRowContainerBackgroundT,
} from './RowContainer.h'

import { styled } from '@linaria/react'
import { BaseCardSC } from 'blocks/Cards'
import { COLOR, FIXED_INDENTS, LAYOUT, MEDIA } from 'vars/index'
import { SpecialRowContainerBackground } from './RowContainer.h'
import { SimpleCarouselScrollableComponentSC } from './RowSimpleContainer/SimpleCarouselScrollable.styled'

export const CarouselWrapperControlSC = styled.div`
  height: 101%;
  align-items: stretch;
  box-sizing: border-box;
  width: calc(
    var(${FIXED_INDENTS.INDENT_100}) + var(${FIXED_INDENTS.INDENT_5})
  );

  [data-arcana-button='true'] {
    width: 100%;
    min-width: 0;
    height: 100%;
    padding: 0;
  }
`

type CarouselSizingProps = {
  gap: ROW_GAP | GRID
  visibleCards: number
  cardAspectRatio: (typeof CARDS_ASPECT_RATIO)[keyof typeof CARDS_ASPECT_RATIO]
  cardAspectRatioVar?: CARDS_ASPECT_RATIO_VARS | EVENT_CARD_ASPECT_RATIO
  matchPosterHeight?: boolean
}

export const CarouselWrapperSC = styled.div<CarouselSizingProps>`
  position: relative;
  /* Query container for --cardWidth: cards are sized from this wrapper's inline
     size (100cqw), not the viewport. 100vw includes the page scrollbar gutter,
     which made cards wider than the row and showed scroll controls even when the
     card count fit exactly (FP-3448). Declared here so in-row scroll controls
     (siblings of the scroller) can use the same poster size (FP-4324). */
  container-type: inline-size;
  --rowGap: ${({ gap }) => ` var(${gap})`};
  --visibleCards: ${({ visibleCards }) => visibleCards};
  --aspectRatio: ${({ cardAspectRatio, cardAspectRatioVar }) =>
    cardAspectRatioVar ? `var(${cardAspectRatioVar})` : cardAspectRatio};
  --cardWidth: calc(
    (
        100cqw - var(--horizontalPaddings) * 2 - (var(--visibleCards) - 1) *
          var(--rowGap)
      ) /
      var(--visibleCards)
  );

  > ${CarouselWrapperControlSC} {
    display: none;

    ${MEDIA.NO_TOUCH} {
      display: inline-flex;
      position: absolute;
      z-index: 3;
      top: ${({ matchPosterHeight }) => (matchPosterHeight ? 0 : '50%')};
      transform: ${({ matchPosterHeight }) =>
        matchPosterHeight ? 'none' : 'translateY(-50%)'};
      height: ${({ matchPosterHeight }) =>
        matchPosterHeight
          ? 'calc(var(--cardWidth) / var(--aspectRatio))'
          : '101%'};

      &:first-child {
        left: 0;
        justify-content: flex-start;
      }

      &:last-child {
        right: 0;
        justify-content: flex-end;
      }
    }
  }
`

export const CarouselScrollableComponentSC = styled.div<{
  gap: ROW_GAP | GRID
  withSpecialCell?: boolean
  visibleCards: number
  extraCount: number
  cellsAlignment?: CellsAlignment
  cardAspectRatio: (typeof CARDS_ASPECT_RATIO)[keyof typeof CARDS_ASPECT_RATIO]
  cardAspectRatioVar?: CARDS_ASPECT_RATIO_VARS | EVENT_CARD_ASPECT_RATIO
  withCardDescription?: boolean
  snapAlign: ISnapAlign
}>`
  overflow: scroll;
  padding: 0 var(--horizontalPaddings);

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none; // firefox

  --cardsCount: ${({ extraCount }) => extraCount};
  --extraCount: calc(var(--cardsCount));
  --rowGap: ${({ gap }) => ` var(${gap})`};
  --visibleCards: ${({ visibleCards }) => visibleCards};
  --aspectRatio: ${({ cardAspectRatio, cardAspectRatioVar }) =>
    cardAspectRatioVar ? `var(${cardAspectRatioVar})` : cardAspectRatio};
  --specialCardWidth: ${({ gap }) =>
    `calc(var(--cardWidth) *  var(--extraCount) + var(${gap}) * ( var(--extraCount) - 1))`};
  display: flex;
  align-items: ${({ cellsAlignment, withCardDescription }) => {
    if (cellsAlignment) return cellsAlignment
    return withCardDescription ? 'flex-start' : 'stretch'
  }};
  gap: var(--rowGap);
  scroll-snap-type: x proximity;

  ${MEDIA.TOUCH} {
    scroll-snap-type: x mandatory;
  }

  > * {
    scroll-margin-left: var(--horizontalPaddings);
    flex: ${({ withCardDescription }) =>
      withCardDescription ? '0 0 var(--cardWidth)' : '0 0 auto'};
    /* Long episode synopsis copy must not widen the flex item and leave a gap
       before the next card (FP-4364). */
    min-width: ${({ withCardDescription }) =>
      withCardDescription ? '0' : 'initial'};
    max-width: ${({ withCardDescription }) =>
      withCardDescription ? 'var(--cardWidth)' : 'none'};
    width: var(--cardWidth);

    scroll-snap-align: ${({ snapAlign }) => snapAlign};

    height: ${({ withCardDescription }) =>
      withCardDescription
        ? 'auto'
        : `calc(var(--cardWidth) / var(--aspectRatio))`};

    &:first-child {
      width: ${({ withSpecialCell }) =>
        withSpecialCell ? 'var(--specialCardWidth)' : 'var(--cardWidth)'};
    }

    ${BaseCardSC} {
      height: calc(var(--cardWidth) / var(--aspectRatio));
      width: var(--cardWidth);
    }
  }
`

export const RowContainerSC = styled.div<{
  horizontalPaddings?: LAYOUT | FIXED_INDENTS | GRID
  verticalPaddingTop?: FIXED_INDENTS | LAYOUT | CAROUSEL_PADDINGS | GRID
  verticalMarginTop?: FIXED_INDENTS | LAYOUT | CAROUSEL_PADDINGS
  verticalMarginBottom?: FIXED_INDENTS | LAYOUT | CAROUSEL_PADDINGS
  verticalPaddingBottom?: FIXED_INDENTS | LAYOUT | CAROUSEL_PADDINGS | GRID
  containerVerticalGap?: RowContainerVerticalGaps
  backgroundStyle?: SpecialRowContainerBackgroundT
  rowPosterImage?: string
  isRenderSpecialCell?: boolean
  backgroundGradient?: string
  matchPosterHeight?: boolean
  withCardDescription?: boolean
}>`
  --horizontalPaddings: ${({
    horizontalPaddings = LAYOUT.HorizontalPaddings,
  }) => `var(${horizontalPaddings})`};
  --verticalPaddingTop: ${({ verticalPaddingTop }) =>
    `var(${verticalPaddingTop})`};
  --verticalMarginTop: ${({ verticalMarginTop, isRenderSpecialCell }) =>
    isRenderSpecialCell ? `var(${verticalMarginTop})` : 0};
  --verticalMarginBottom: ${({ verticalMarginBottom, isRenderSpecialCell }) =>
    isRenderSpecialCell ? `var(${verticalMarginBottom})` : 0};
  --verticalPaddingBottom: ${({ verticalPaddingBottom }) =>
    `var(${verticalPaddingBottom})`};
  --containerVerticalGap: ${({ containerVerticalGap }) =>
    `var(${containerVerticalGap})`};
  width: 100%;
  overflow: ${({ withCardDescription }) =>
    withCardDescription ? 'visible' : 'hidden'};
  display: flex;
  flex-direction: column;
  padding-block-start: var(--verticalPaddingTop);

  ${CarouselWrapperSC} {
    & > ${SimpleCarouselScrollableComponentSC} {
      padding-top: var(--containerVerticalGap);
      /* margin-top: calc(0px - var(--containerVerticalGap)); */
      padding-bottom: var(--verticalPaddingBottom);
      /* margin-bottom: calc(0px - var(--verticalPaddingBottom)); */
    }

    & > ${CarouselScrollableComponentSC} {
      padding-top: var(--containerVerticalGap);
      /* margin-top: calc(0px - var(--containerVerticalGap)); */
      padding-bottom: var(--verticalPaddingBottom);
      /* margin-bottom: calc(0px - var(--verticalPaddingBottom)); */
    }

    & > ${CarouselWrapperControlSC} {
      padding-top: var(--containerVerticalGap);
      padding-bottom: ${({ matchPosterHeight }) =>
        matchPosterHeight ? 0 : 'var(--verticalPaddingBottom)'};
    }
  }

  background: ${({ backgroundStyle, rowPosterImage, backgroundGradient }) => {
    switch (backgroundStyle) {
      case SpecialRowContainerBackground.Gradient:
        return `var(${COLOR.MAIN_GRADIENT_0}), var(${COLOR.ACCENT_PRIMARY_GRADIENT})`
      case SpecialRowContainerBackground.GradientWithOverlay:
        return ` var(${COLOR.MAIN_GRADIENT_0}), var(${COLOR.MAIN_GRADIENT_90}),  var(${COLOR.ACCENT_PRIMARY_GRADIENT}) `
      case SpecialRowContainerBackground.FullWidthImage:
        return `var(${COLOR.MAIN_GRADIENT_90}), var(${COLOR.MAIN_GRADIENT_0}), url(${rowPosterImage}) no-repeat left top/ 100% `
      case SpecialRowContainerBackground.HalfWidthImage:
        return `var(${COLOR.MAIN_GRADIENT_0}), var(${COLOR.MAIN_GRADIENT_270}) left/51%, var(${COLOR.MAIN_GRADIENT_90}) left/51%, url(${rowPosterImage}) no-repeat left/50% `
      case SpecialRowContainerBackground.SimpleFullWitdhImage:
        return `var(${COLOR.ROW_MAIN_GRADIENT_0}), var(${COLOR.ROW_MAIN_GRADIENT_1}), url(${rowPosterImage}) no-repeat center / 100%`
      case SpecialRowContainerBackground.SimpleGradient:
        return `var(${COLOR.ROW_MAIN_GRADIENT_0}), var(${COLOR.ROW_MAIN_GRADIENT_1}), ${backgroundGradient}`
      case SpecialRowContainerBackground.ConstantDark:
        return `var(${COLOR.SURFACE_DARK})`
      default:
        return 'none'
    }
  }};
  background-size: ${({ backgroundStyle }) => {
    return backgroundStyle ===
      SpecialRowContainerBackground.SimpleFullWitdhImage
      ? 'cover'
      : 'initial'
  }};

  flex-shrink: 0;
  margin-top: var(--verticalMarginTop);
  margin-bottom: var(--verticalMarginBottom);
`

export const RowTitleSC = styled.div`
  padding: 0 var(--horizontalPaddings);
`
